// ObligationDetailPage — framework detail with obligation structure tree and item detail pane.
// Rendered in the right panel of ObligationLayout.
// Route: /t/:slug/obligations/:frameworkId
//
// Layout: framework header at top, then a nested two-panel split below:
//   Left sub-panel: obligation structure tree (requirement_group → sub_requirement → title)
//   Right sub-panel: item detail pane (blank initially, populated when leaf node selected)

import { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  obligationsApi,
  coverageLevels,
  coverageLevelLabels,
  coverageLevelColors,
  type FrameworkItem,
} from '@/lib/api/obligations'
import { controlsApi } from '@/lib/api/controls'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  ArrowLeft,
  ChevronRight,
  ChevronDown,
  ChevronsUpDown,
  FolderOpen,
  Folder,
  Filter,
  Pencil,
  Plus,
  Trash2,
  X,
  Search,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

// --- Obligation tree node types ---

interface RequirementGroupNode {
  name: string
  subGroups: SubRequirementNode[]
  itemCount: number
  mappedCount: number
}

interface SubRequirementNode {
  name: string
  items: FrameworkItem[]
  itemCount: number
  mappedCount: number
}

/** Build three-level tree from flat item list. */
function buildObligationTree(items: FrameworkItem[]): RequirementGroupNode[] {
  const groupMap = new Map<string, Map<string, FrameworkItem[]>>()

  for (const item of items) {
    const group = item.requirement_group || '(Ungrouped)'
    const sub = item.sub_requirement || '(General)'

    if (!groupMap.has(group)) groupMap.set(group, new Map())
    const subMap = groupMap.get(group)!
    if (!subMap.has(sub)) subMap.set(sub, [])
    subMap.get(sub)!.push(item)
  }

  const result: RequirementGroupNode[] = []
  for (const [groupName, subMap] of groupMap) {
    const subGroups: SubRequirementNode[] = []
    let groupItemCount = 0
    let groupMappedCount = 0

    for (const [subName, subItems] of subMap) {
      const mapped = subItems.filter((i) => i.is_mapped).length
      subGroups.push({
        name: subName,
        items: subItems,
        itemCount: subItems.length,
        mappedCount: mapped,
      })
      groupItemCount += subItems.length
      groupMappedCount += mapped
    }

    result.push({
      name: groupName,
      subGroups,
      itemCount: groupItemCount,
      mappedCount: groupMappedCount,
    })
  }

  return result
}

// --- Main component ---

export function ObligationDetailPage() {
  const { slug } = useTenant()
  const navigate = useNavigate()
  const params = useParams({ strict: false }) as { frameworkId?: string }
  const frameworkId = params.frameworkId ?? ''
  const api = useApi()
  const oblApi = obligationsApi(api)
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [showUnmappedOnly, setShowUnmappedOnly] = useState(false)

  // Framework detail
  const {
    data: framework,
    error: frameworkError,
    isLoading: frameworkLoading,
  } = useQuery({
    queryKey: queryKeys.complianceFrameworks.detail(slug, frameworkId),
    queryFn: () => oblApi.get(frameworkId),
    enabled: !!frameworkId,
  })

  // Items (all or unmapped)
  const {
    data: items,
    error: itemsError,
    isLoading: itemsLoading,
  } = useQuery({
    queryKey: showUnmappedOnly
      ? queryKeys.complianceFrameworks.unmappedItems(slug, frameworkId)
      : queryKeys.complianceFrameworks.items(slug, frameworkId),
    queryFn: () =>
      showUnmappedOnly
        ? oblApi.listUnmappedItems(frameworkId)
        : oblApi.listItems(frameworkId),
    enabled: !!frameworkId,
  })

  const tree = useMemo(() => (items ? buildObligationTree(items) : []), [items])

  const handleBack = useCallback(() => {
    navigate({ to: `/t/${slug}/obligations` })
  }, [navigate, slug])

  if (frameworkLoading) {
    return (
      <div className="p-4">
        <TableSkeleton rows={3} />
      </div>
    )
  }

  if (frameworkError) {
    return (
      <div className="p-4">
        <ErrorState
          message="Failed to load framework"
          technicalDetail={frameworkError.message}
        />
      </div>
    )
  }

  if (!framework) return null

  const cs = framework.coverage_summary

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={handleBack} className="h-8 w-8">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-lg font-semibold">{framework.name}</h2>
            {framework.description && (
              <p className="truncate text-sm text-muted-foreground">{framework.description}</p>
            )}
          </div>
        </div>

        {/* Coverage summary cards */}
        <div className="mt-3 flex gap-2">
          <CoverageCard label="Full" count={cs.full} color="bg-green-100 text-green-700" />
          <CoverageCard label="Partial" count={cs.partial} color="bg-yellow-100 text-yellow-700" />
          <CoverageCard label="Planned" count={cs.planned} color="bg-blue-100 text-blue-700" />
          <CoverageCard label="Not Assessed" count={cs.not_assessed} color="bg-gray-100 text-gray-600" />
          <CoverageCard label="Unmapped" count={cs.unmapped} color="bg-red-100 text-red-700" />
        </div>
      </div>

      {/* Nested two-panel split: obligation tree + item detail */}
      <div className="flex min-h-0 flex-1">
        {/* Left sub-panel: obligation structure tree */}
        <div className="flex w-96 shrink-0 flex-col border-r" style={{ minWidth: '280px', maxWidth: '50%', resize: 'horizontal', overflow: 'auto' }}>
          <div className="flex items-center justify-between border-b px-3 py-2">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Requirements
            </span>
            <Button
              variant={showUnmappedOnly ? 'secondary' : 'ghost'}
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                setShowUnmappedOnly(!showUnmappedOnly)
                setSelectedItemId(null)
              }}
            >
              <Filter className="mr-1 h-3 w-3" />
              {showUnmappedOnly ? 'Unmapped' : 'All'}
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {itemsLoading ? (
              <div className="space-y-2 p-3">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="h-6 animate-pulse rounded bg-muted/50" />
                ))}
              </div>
            ) : itemsError ? (
              <div className="p-3">
                <ErrorState
                  message="Failed to load items"
                  technicalDetail={itemsError.message}
                />
              </div>
            ) : tree.length === 0 ? (
              <div className="p-3 text-center text-sm text-muted-foreground">
                {showUnmappedOnly ? 'All items are mapped.' : 'No requirements found.'}
              </div>
            ) : (
              <ObligationTree
                tree={tree}
                selectedItemId={selectedItemId}
                onSelectItem={setSelectedItemId}
              />
            )}
          </div>
        </div>

        {/* Right sub-panel: item detail */}
        <div className="flex-1 overflow-y-auto">
          {selectedItemId ? (
            <ItemDetailPane
              frameworkId={frameworkId}
              itemId={selectedItemId}
              slug={slug}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              <p className="text-sm">Select a requirement to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// --- Coverage summary card ---

function CoverageCard({
  label,
  count,
  color,
}: {
  label: string
  count: number
  color: string
}) {
  return (
    <div className={cn('rounded-md px-3 py-1.5 text-center', color)}>
      <div className="text-lg font-semibold tabular-nums">{count}</div>
      <div className="text-xs">{label}</div>
    </div>
  )
}

// --- Obligation structure tree ---

interface ObligationTreeProps {
  tree: RequirementGroupNode[]
  selectedItemId: string | null
  onSelectItem: (itemId: string) => void
}

function ObligationTree({ tree, selectedItemId, onSelectItem }: ObligationTreeProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [expandedSubs, setExpandedSubs] = useState<Set<string>>(new Set())

  // Compute whether everything is currently expanded
  const allGroupKeys = useMemo(() => tree.map((g) => g.name), [tree])
  const allSubKeys = useMemo(
    () => tree.flatMap((g) => g.subGroups.map((s) => `${g.name}::${s.name}`)),
    [tree],
  )
  const isAllExpanded =
    allGroupKeys.length > 0 &&
    allGroupKeys.every((k) => expandedGroups.has(k)) &&
    allSubKeys.every((k) => expandedSubs.has(k))

  const toggleAll = useCallback(() => {
    if (isAllExpanded) {
      setExpandedGroups(new Set())
      setExpandedSubs(new Set())
    } else {
      setExpandedGroups(new Set(allGroupKeys))
      setExpandedSubs(new Set(allSubKeys))
    }
  }, [isAllExpanded, allGroupKeys, allSubKeys])

  const toggleGroup = useCallback((name: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }, [])

  const toggleSub = useCallback((key: string) => {
    setExpandedSubs((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  return (
    <div className="py-1">
      <div className="flex justify-end px-2 pb-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-muted-foreground"
          onClick={toggleAll}
        >
          <ChevronsUpDown className="mr-1 h-3 w-3" />
          {isAllExpanded ? 'Collapse all' : 'Expand all'}
        </Button>
      </div>
      {tree.map((group) => {
        const isGroupExpanded = expandedGroups.has(group.name)
        return (
          <div key={group.name}>
            {/* Level 1: requirement_group */}
            <div
              className="flex cursor-pointer items-center gap-1.5 px-2 py-1.5 text-sm font-medium hover:bg-accent/50"
              onClick={() => toggleGroup(group.name)}
            >
              {isGroupExpanded ? (
                <ChevronDown className="h-3.5 w-3.5 shrink-0" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 shrink-0" />
              )}
              {isGroupExpanded ? (
                <FolderOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              ) : (
                <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              )}
              <span className="truncate">{group.name}</span>
              <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                {group.mappedCount}/{group.itemCount}
              </span>
            </div>

            {isGroupExpanded &&
              group.subGroups.map((sub) => {
                const subKey = `${group.name}::${sub.name}`
                const isSubExpanded = expandedSubs.has(subKey)
                return (
                  <div key={subKey}>
                    {/* Level 2: sub_requirement */}
                    <div
                      className="flex cursor-pointer items-center gap-1.5 py-1.5 pl-6 pr-2 text-sm hover:bg-accent/50"
                      onClick={() => toggleSub(subKey)}
                    >
                      {isSubExpanded ? (
                        <ChevronDown className="h-3 w-3 shrink-0" />
                      ) : (
                        <ChevronRight className="h-3 w-3 shrink-0" />
                      )}
                      <span className="truncate text-muted-foreground">{sub.name}</span>
                      <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                        {sub.mappedCount}/{sub.itemCount}
                      </span>
                    </div>

                    {isSubExpanded &&
                      sub.items.map((item) => (
                        <div
                          key={item.id}
                          className={cn(
                            'flex cursor-pointer items-center gap-1.5 py-1.5 pl-10 pr-2 text-sm transition-colors',
                            'hover:bg-accent/50',
                            selectedItemId === item.id && 'bg-primary/10 text-primary',
                            !item.is_mapped && selectedItemId !== item.id && 'font-semibold text-red-600',
                          )}
                          onClick={() => onSelectItem(item.id)}
                        >
                          {/* Level 3: requirement (leaf) */}
                          <span className="truncate">{item.title}</span>
                          <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                            {item.reference_code}
                          </span>
                        </div>
                      ))}
                  </div>
                )
              })}
          </div>
        )
      })}
    </div>
  )
}

// --- Item detail pane ---

interface ItemDetailPaneProps {
  frameworkId: string
  itemId: string
  slug: string
}

function ItemDetailPane({ frameworkId, itemId, slug }: ItemDetailPaneProps) {
  const api = useApi()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const oblApi = obligationsApi(api)
  const [addMappingOpen, setAddMappingOpen] = useState(false)
  const [editingMapping, setEditingMapping] = useState<{
    id: string
    control_id: string
    control_name: string
    coverage_level: string
    coverage_notes: string | null
  } | null>(null)
  const [removingMappingId, setRemovingMappingId] = useState<string | null>(null)

  const {
    data: item,
    error,
    isLoading,
  } = useQuery({
    queryKey: queryKeys.complianceFrameworks.itemDetail(slug, frameworkId, itemId),
    queryFn: () => oblApi.getItem(frameworkId, itemId),
    enabled: !!itemId,
  })

  const removeMutation = useMutation({
    mutationFn: (mappingId: string) => oblApi.deleteMapping(frameworkId, mappingId),
    onSuccess: () => {
      setRemovingMappingId(null)
      queryClient.invalidateQueries({ queryKey: queryKeys.complianceFrameworks.itemDetail(slug, frameworkId, itemId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.complianceFrameworks.items(slug, frameworkId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.complianceFrameworks.detail(slug, frameworkId) })
    },
  })

  function handleMappingSaved() {
    setAddMappingOpen(false)
    setEditingMapping(null)
    queryClient.invalidateQueries({ queryKey: queryKeys.complianceFrameworks.itemDetail(slug, frameworkId, itemId) })
    queryClient.invalidateQueries({ queryKey: queryKeys.complianceFrameworks.items(slug, frameworkId) })
    queryClient.invalidateQueries({ queryKey: queryKeys.complianceFrameworks.detail(slug, frameworkId) })
  }

  if (isLoading) {
    return (
      <div className="p-4">
        <TableSkeleton rows={3} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4">
        <ErrorState
          message="Failed to load item"
          technicalDetail={error.message}
        />
      </div>
    )
  }

  if (!item) return null

  return (
    <div className="space-y-6 p-4">
      {/* Item header */}
      <div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="font-mono text-xs">
            {item.reference_code}
          </Badge>
          {item.is_mapped ? (
            <Badge className="bg-green-100 text-green-700">Mapped</Badge>
          ) : (
            <Badge variant="secondary">Unmapped</Badge>
          )}
        </div>
        <h3 className="mt-2 text-lg font-semibold">{item.title}</h3>
        {item.description && (
          <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
        )}
      </div>

      {/* Requirement context */}
      <div className="rounded-md border p-3 text-sm">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <span className="text-muted-foreground">Requirement Group:</span>{' '}
            <span className="font-medium">{item.requirement_group}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Sub-requirement:</span>{' '}
            <span className="font-medium">{item.sub_requirement || '—'}</span>
          </div>
        </div>
      </div>

      {/* Tenant interpretation (inline editable) */}
      <TenantInterpretationEditor
        frameworkId={frameworkId}
        itemId={itemId}
        slug={slug}
        value={item.tenant_interpretation ?? ''}
      />

      {/* Mappings */}
      <div>
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">
            Control Mappings ({item.mappings.length})
          </h4>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAddMappingOpen(true)}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add Mapping
          </Button>
        </div>

        {item.mappings.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            No controls mapped to this requirement.
          </p>
        ) : (
          <div className="mt-2 space-y-2">
            {item.mappings.map((mapping) => (
              <div
                key={mapping.id}
                className="rounded-md border p-3"
              >
                <div className="flex items-center justify-between">
                  <button
                    className="font-medium text-primary underline-offset-4 hover:underline"
                    onClick={() => navigate({ to: `/t/${slug}/controls/${mapping.control_id}` })}
                  >
                    {mapping.control_name}
                  </button>
                  <div className="flex items-center gap-2">
                    <Badge className={coverageLevelColors[mapping.coverage_level] ?? ''}>
                      {coverageLevelLabels[mapping.coverage_level] ?? mapping.coverage_level}
                    </Badge>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                            onClick={() => setEditingMapping({
                              id: mapping.id,
                              control_id: mapping.control_id,
                              control_name: mapping.control_name,
                              coverage_level: mapping.coverage_level,
                              coverage_notes: mapping.coverage_notes ?? null,
                            })}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Edit mapping</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => setRemovingMappingId(mapping.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Remove mapping</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
                {mapping.coverage_notes && (
                  <p className="mt-1 text-sm text-muted-foreground">{mapping.coverage_notes}</p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  Mapped by {mapping.mapped_by_name} on{' '}
                  {new Date(mapping.mapped_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add mapping dialog */}
      <MappingDialog
        open={addMappingOpen}
        frameworkId={frameworkId}
        itemId={itemId}
        onClose={() => setAddMappingOpen(false)}
        onSaved={handleMappingSaved}
      />

      {/* Edit mapping dialog */}
      <MappingDialog
        open={!!editingMapping}
        frameworkId={frameworkId}
        itemId={itemId}
        editMapping={editingMapping ?? undefined}
        onClose={() => setEditingMapping(null)}
        onSaved={handleMappingSaved}
      />

      {/* Remove mapping confirmation */}
      {removingMappingId && (
        <RemoveMappingConfirm
          isPending={removeMutation.isPending}
          onConfirm={() => removeMutation.mutate(removingMappingId)}
          onCancel={() => setRemovingMappingId(null)}
        />
      )}
    </div>
  )
}

// --- Tenant interpretation inline editor ---

function TenantInterpretationEditor({
  frameworkId,
  itemId,
  slug,
  value,
}: {
  frameworkId: string
  itemId: string
  slug: string
  value: string
}) {
  const api = useApi()
  const queryClient = useQueryClient()
  const oblApi = obligationsApi(api)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [error, setError] = useState<string | null>(null)

  // Sync draft when value changes (e.g. from refetch)
  useEffect(() => {
    if (!editing) setDraft(value)
  }, [value, editing])

  const mutation = useMutation({
    mutationFn: () =>
      oblApi.updateInterpretation(frameworkId, itemId, {
        tenant_interpretation: draft.trim() || null,
      }),
    onSuccess: () => {
      setEditing(false)
      setError(null)
      queryClient.invalidateQueries({
        queryKey: queryKeys.complianceFrameworks.itemDetail(slug, frameworkId, itemId),
      })
    },
    onError: (err: Error) => setError(err.message),
  })

  if (!editing) {
    return (
      <div>
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium">Organisation Interpretation</h4>
          <button
            className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            onClick={() => {
              setDraft(value)
              setEditing(true)
            }}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {value || 'No interpretation provided.'}
        </p>
      </div>
    )
  }

  return (
    <div>
      <h4 className="text-sm font-medium">Organisation Interpretation</h4>
      {error && (
        <div className="mt-1 rounded bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
      )}
      <textarea
        className="mt-1 min-h-[80px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="How does this requirement apply to your organisation?"
        autoFocus
      />
      <div className="mt-2 flex gap-2">
        <Button
          size="sm"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Saving...' : 'Save'}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setEditing(false)
            setError(null)
          }}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
      </div>
    </div>
  )
}

// --- Mapping dialog (create & edit) ---

interface MappingDialogProps {
  open: boolean
  frameworkId: string
  itemId: string
  /** When set, the dialog is in edit mode with these initial values. */
  editMapping?: {
    id: string
    control_id: string
    control_name: string
    coverage_level: string
    coverage_notes: string | null
  }
  onClose: () => void
  onSaved: () => void
}

function MappingDialog({ open, frameworkId, itemId, editMapping, onClose, onSaved }: MappingDialogProps) {
  const api = useApi()
  const { slug } = useTenant()
  const oblApi = obligationsApi(api)
  const ctrlApi = controlsApi(api)

  const isEdit = !!editMapping

  const [search, setSearch] = useState('')
  const [selectedControlId, setSelectedControlId] = useState(editMapping?.control_id ?? '')
  const [coverageLevel, setCoverageLevel] = useState<string>(editMapping?.coverage_level ?? 'full')
  const [coverageNotes, setCoverageNotes] = useState(editMapping?.coverage_notes ?? '')
  const [error, setError] = useState<string | null>(null)
  const [initialised, setInitialised] = useState(false)

  // Reset form state when dialog opens with new props
  if (open && !initialised) {
    setSelectedControlId(editMapping?.control_id ?? '')
    setCoverageLevel(editMapping?.coverage_level ?? 'full')
    setCoverageNotes(editMapping?.coverage_notes ?? '')
    setSearch('')
    setError(null)
    setInitialised(true)
  }
  if (!open && initialised) {
    setInitialised(false)
  }

  // Fetch available controls
  const { data: controls, isLoading: controlsLoading } = useQuery({
    queryKey: queryKeys.controls.all(slug),
    queryFn: () => ctrlApi.list({ lifecycle_state: 'active' }),
    enabled: open,
  })

  const filtered = useMemo(() => {
    if (!controls) return []
    if (!search) return controls
    const q = search.toLowerCase()
    return controls.filter(
      (c) => c.name.toLowerCase().includes(q) || c.category_name.toLowerCase().includes(q),
    )
  }, [controls, search])

  const createMutation = useMutation({
    mutationFn: () => {
      if (!selectedControlId) throw new Error('Select a control')
      return oblApi.createMapping(frameworkId, {
        control_id: selectedControlId,
        compliance_framework_item_id: itemId,
        coverage_level: coverageLevel as 'full' | 'partial' | 'planned' | 'not_assessed',
        coverage_notes: coverageNotes.trim() || undefined,
      })
    },
    onSuccess: () => {
      handleClose()
      onSaved()
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!editMapping) throw new Error('No mapping to update')
      return oblApi.updateMapping(frameworkId, editMapping.id, {
        coverage_level: coverageLevel as 'full' | 'partial' | 'planned' | 'not_assessed',
        coverage_notes: coverageNotes.trim() || undefined,
      })
    },
    onSuccess: () => {
      handleClose()
      onSaved()
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  function handleClose() {
    setInitialised(false)
    onClose()
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-lg rounded-lg border bg-background shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">
            {isEdit ? 'Edit Control Mapping' : 'Add Control Mapping'}
          </h2>
          <button className="rounded-sm p-1 hover:bg-accent" onClick={handleClose}>
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <div className="space-y-4 px-6 py-4">
          {error && (
            <div className="rounded bg-destructive/10 p-2 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Control search & select — disabled in edit mode */}
          <div>
            <Label>Control *</Label>
            {isEdit ? (
              <div className="mt-1 rounded-md border bg-muted/50 px-3 py-2 text-sm">
                {editMapping.control_name}
              </div>
            ) : (
              <>
                <div className="relative mt-1">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search controls..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="mt-2 max-h-48 overflow-y-auto rounded-md border">
                  {controlsLoading ? (
                    <div className="p-3 text-center text-sm text-muted-foreground">Loading controls...</div>
                  ) : filtered.length === 0 ? (
                    <div className="p-3 text-center text-sm text-muted-foreground">
                      {search ? 'No controls match your search' : 'No active controls found'}
                    </div>
                  ) : (
                    filtered.map((control) => (
                      <div
                        key={control.id}
                        className={cn(
                          'flex cursor-pointer items-center justify-between px-3 py-2 text-sm transition-colors',
                          'hover:bg-accent/50',
                          selectedControlId === control.id && 'bg-primary/10 font-medium text-primary',
                        )}
                        onClick={() => setSelectedControlId(control.id)}
                      >
                        <span>{control.name}</span>
                        <span className="text-xs text-muted-foreground">{control.category_name}</span>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          {/* Coverage level */}
          <div>
            <Label htmlFor="coverage-level">Coverage Level *</Label>
            <select
              id="coverage-level"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={coverageLevel}
              onChange={(e) => setCoverageLevel(e.target.value)}
            >
              {coverageLevels.map((level) => (
                <option key={level} value={level}>
                  {coverageLevelLabels[level]}
                </option>
              ))}
            </select>
          </div>

          {/* Coverage notes */}
          <div>
            <Label htmlFor="coverage-notes">Notes</Label>
            <textarea
              id="coverage-notes"
              className="mt-1 min-h-[60px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Optional notes about how this control covers the requirement"
              value={coverageNotes}
              onChange={(e) => setCoverageNotes(e.target.value)}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t px-6 py-4">
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => (isEdit ? updateMutation.mutate() : createMutation.mutate())}
            disabled={isPending || (!isEdit && !selectedControlId)}
          >
            {isPending
              ? isEdit ? 'Saving...' : 'Adding...'
              : isEdit ? 'Save Changes' : 'Add Mapping'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// --- Remove mapping confirmation ---

interface RemoveMappingConfirmProps {
  isPending: boolean
  onConfirm: () => void
  onCancel: () => void
}

function RemoveMappingConfirm({ isPending, onConfirm, onCancel }: RemoveMappingConfirmProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-sm rounded-lg border bg-background shadow-lg">
        <div className="px-6 py-4">
          <h3 className="text-lg font-semibold">Remove Mapping</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Are you sure you want to remove this control mapping? This action can be reversed by re-adding the mapping.
          </p>
        </div>
        <div className="flex justify-end gap-2 border-t px-6 py-4">
          <Button variant="outline" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isPending}>
            {isPending ? 'Removing...' : 'Remove'}
          </Button>
        </div>
      </div>
    </div>
  )
}
